import { Checkbox, Separator, Typography } from '@s4l/ds'
import React, { useMemo } from 'react'
import { usePriceDetailsHelpers } from '../hooks'
import PaymentRow from '../PaymentRow'
import RoomSection from '../RoomSection'
import styles from '../styles.module.css'
import { PAYMENT_DISPLAY_TYPES, type RoomData, type TotalData, type WalletSummary } from '../types'
import {
  calculateAggregatedTotals,
  getAllTotalsByPayment,
  getBattlefaceLabel,
  getNotIncludedTaxCurrency,
  groupTaxDetails,
} from '../utils'

interface PriceDetailsContentProps {
  nights: number
  rooms: RoomData[]
  totalData?: TotalData
  wallet?: WalletSummary
  onWalletToggle?: (nextSelected: boolean) => void
  isNewExperimentVariant?: boolean
}

export default function PriceDetailsContent({
  nights,
  rooms,
  totalData,
  wallet,
  onWalletToggle,
  isNewExperimentVariant,
}: Readonly<PriceDetailsContentProps>) {
  const { formatCurrency, formatMessage, formatMessageCapitalized, formatDate } =
    usePriceDetailsHelpers()

  // Enrich rooms with data from totalData.availabilities when available
  const enrichedRooms = useMemo(() => {
    if (!totalData?.availabilities || totalData.availabilities.length === 0) {
      return rooms
    }

    return rooms.map(room => {
      const matchingAvailability = totalData.availabilities?.[0]
      if (!matchingAvailability?.summary) {
        return room
      }

      // Check if availability has valid taxes (exists, has items, and has amounts > 0)
      const hasValidTaxes =
        matchingAvailability.taxes &&
        matchingAvailability.taxes.length > 0 &&
        matchingAvailability.taxes.some(tax => tax.amount > 0)

      // If availability exists with summary but has no valid taxes (empty array or all zeros),
      // we should clear the taxDetails to ensure we don't show stale tax data
      if (!hasValidTaxes) {
        return {
          ...room,
          subtotal: matchingAvailability.summary.subtotal?.amount ?? room.subtotal,
          total: matchingAvailability.summary.total?.amount ?? room.total,
          totalWithFees: matchingAvailability.summary.total_with_fees?.amount ?? room.totalWithFees,
          taxDetails: [], // Clear tax details when availability has no valid taxes
        }
      }

      // If we have valid taxes, enrich with them
      const updatedTaxDetails = (matchingAvailability.taxes || [])
        .filter(tax => tax.amount > 0) // Only include taxes with amount > 0
        .map(tax => ({
          amount: tax.amount,
          currency: tax.currency,
          included: tax.included,
          name: tax.name,
        }))

      // Update room with fresh data from availability
      return {
        ...room,
        subtotal: matchingAvailability.summary.subtotal?.amount ?? room.subtotal,
        total: matchingAvailability.summary.total?.amount ?? room.total,
        totalWithFees: matchingAvailability.summary.total_with_fees?.amount ?? room.totalWithFees,
        taxDetails: updatedTaxDetails,
      }
    })
  }, [rooms, totalData])

  // Calculate aggregated data from enriched rooms array
  const aggregatedData = useMemo(() => {
    if (!enrichedRooms || enrichedRooms.length === 0) {
      return null
    }
    return calculateAggregatedTotals(enrichedRooms)
  }, [enrichedRooms])

  const groupedTaxDetails = useMemo(
    () => (aggregatedData?.taxDetails ? groupTaxDetails(aggregatedData.taxDetails) : []),
    [aggregatedData?.taxDetails]
  )

  const paymentTotals = useMemo(() => getAllTotalsByPayment(groupedTaxDetails), [groupedTaxDetails])

  const payAtPropertyCurrency = useMemo(
    () =>
      aggregatedData?.taxDetails ? getNotIncludedTaxCurrency(aggregatedData.taxDetails) : undefined,
    [aggregatedData?.taxDetails]
  )

  const battlefaceProducts = useMemo(
    () =>
      totalData?.additionalProducts
        ? Object.entries(totalData.additionalProducts).filter(
            ([key, value]) => key.startsWith('battleface') && typeof value === 'number' && value > 0
          )
        : [],
    [totalData?.additionalProducts]
  )

  if (!aggregatedData || !nights || nights <= 0) {
    return null
  }

  const {
    totalCurrency,
    payDate,
    hasInsurance,
    insuranceAmount,
    insuranceCurrency,
    totalWithInsurance,
    payNowWithInsurance,
    payLate,
    roomsNumber,
    isMultiRoom,
    paymentDisplayType,
  } = aggregatedData

  // Totals
  const totalPrice = totalData?.totalWithFees || totalWithInsurance
  const totalInsurance = totalData?.insurance || insuranceAmount
  const totalInsuranceCurrency = totalData?.insuranceCurrency || insuranceCurrency
  const pdpAmount = totalData?.pdp || 0
  const pdpCurrency = totalData?.pdpCurrency || totalCurrency

  // Display sections logic
  const displayInsuranceSection = hasInsurance
  const displayPdpSection = Boolean(pdpAmount > 0)
  const displayWalletSection = Boolean(
    wallet && wallet.isVisible && wallet.availableAmount && wallet.availableAmount > 0
  )
  const displayBattlefaceSection = battlefaceProducts.length > 0
  const displayPayAtPropertySection = Boolean(paymentTotals.payLater > 0)
  // Only show tax details button if we have taxes AND they have actual amounts > 0
  const displayTaxDetailsButton = Boolean(
    groupedTaxDetails &&
      groupedTaxDetails.length > 0 &&
      groupedTaxDetails.some(tax => (tax.client?.amount ?? tax.amount) > 0)
  )

  // Labels and texts
  const formattedPayDate = payDate ? formatDate(payDate) : undefined
  const localCurrencyText =
    displayPayAtPropertySection && payAtPropertyCurrency
      ? `${formatMessage({ id: 'global.local-currency' })} ${formatMessage(
          { id: 'tax-breakdown.payment-at-property' },
          { currency: payAtPropertyCurrency }
        )}`
      : formatMessage({ id: 'global.local-currency' })
  const payNowLabel = formatMessage({ id: 'global.pay-now' })
  const depositSuffix =
    paymentDisplayType === PAYMENT_DISPLAY_TYPES.STAY_FOR_LONG_DEPOSIT
      ? ` (${formatMessage({ id: 'policies.deposit' })})`
      : ''

  const handleWalletChange = () => {
    if (wallet?.loading) return

    onWalletToggle?.(!wallet?.selected)
  }

  const renderPaymentsByDate = () => (
    <>
      {aggregatedData?.paymentsByDate?.map((payment, index) => {
        const totalPayLate = totalData?.payLate ? totalData.payLate : payment.amount

        return (
          <PaymentRow
            key={payment.date || `payment-${index}`}
            label={formatMessageCapitalized(
              { id: 'policies.pay-on' },
              { date: formatDate(payment.date) }
            )}
            amount={totalPayLate}
            currency={payment.currency}
            isNewExperimentVariant={isNewExperimentVariant}
          />
        )
      })}
    </>
  )

  const renderPayNowRow = (amount: number, currency: string) => (
    <PaymentRow
      label={payNowLabel}
      amount={amount}
      currency={currency}
      isNewExperimentVariant={isNewExperimentVariant}
    />
  )

  return (
    <>
      <div className={styles.section}>
        {enrichedRooms.map((room, index) => (
          <RoomSection
            key={`room-${index + 1}`}
            room={room}
            index={index}
            nights={nights}
            isMultiRoom={isMultiRoom}
            isNewExperimentVariant={isNewExperimentVariant}
          />
        ))}

        {(displayInsuranceSection ||
          displayPdpSection ||
          displayWalletSection ||
          displayBattlefaceSection) && (
          <div className={styles.sections}>
            {displayBattlefaceSection &&
              battlefaceProducts.map(([key, value]) => (
                <div key={key} className={styles.insuranceRow}>
                  <Typography variant="body" size="l">
                    {formatMessage(
                      {
                        id: 'insurance-package.summary',
                      },
                      { insurance: formatMessage({ id: getBattlefaceLabel(key) }) }
                    )}
                  </Typography>
                  <Typography variant="body" size="l">
                    {formatCurrency(value, totalCurrency)}
                  </Typography>
                </div>
              ))}

            {displayInsuranceSection && (
              <div className={styles.insuranceRow}>
                <Typography variant="body" size="l">
                  {formatMessage({ id: 'insurance.refund-protect' })}
                </Typography>
                <Typography variant="body" size="l">
                  {formatCurrency(totalInsurance, totalInsuranceCurrency)}
                </Typography>
              </div>
            )}

            {displayPdpSection && (
              <div className={styles.insuranceRow}>
                <Typography variant="body" size="l">
                  {formatMessage({ id: 'insurance.price-drop-protection' })}
                </Typography>
                <Typography variant="body" size="l">
                  {formatCurrency(pdpAmount, pdpCurrency)}
                </Typography>
              </div>
            )}

            {displayWalletSection && (
              <div className={styles.insuranceRow}>
                <Checkbox
                  name="staywallet"
                  onChange={handleWalletChange}
                  selected={Boolean(wallet?.selected)}
                  disabled={wallet?.loading}
                >
                  <Typography variant="body" size="l">
                    {`${formatMessage({ id: 'serp.filter-apply' })} ${formatMessage({ id: 'staywallet.credit' })}`}
                  </Typography>
                </Checkbox>
                <Typography variant="body" size="l" bold color="var(--color-success-500)">
                  {`-${formatCurrency(wallet?.availableAmount, wallet?.currency)}`}
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>

      <Separator className={styles.separator} />

      <div className={styles.sectionTotal}>
        <div className={styles.totalHeader}>
          {isNewExperimentVariant ? (
            <>
              <Typography variant="body" size="l" color="var(--color-neutral-800)" bold>
                {formatMessage({ id: 'global.total' })}
                {totalCurrency ? ` (${totalCurrency})` : ''}
              </Typography>
              <Typography variant="body" size="l" color="var(--color-neutral-800)" bold>
                {formatCurrency(totalPrice, totalCurrency)}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="title" size="xs" bold>
                {formatMessage({ id: 'global.total' })}
                {totalCurrency ? ` (${totalCurrency})` : ''}
              </Typography>
              <Typography variant="title" size="xs" bold>
                {formatCurrency(totalPrice, totalCurrency)}
              </Typography>
            </>
          )}
        </div>

        <div className={styles.subHeader}>
          <Typography variant="caption" size="l">
            {`${formatMessage({ id: 'global.nights' }, { nights })}, ${formatMessage({ id: 'global.rooms-plural' }, { rooms: roomsNumber })}`}
          </Typography>
          <Typography variant="caption" size="l">
            {formatMessage({ id: 'global.taxes-fees-reduced' })}
          </Typography>
        </div>

        <div className={styles.detailsBlockDark}>
          {(() => {
            if (totalData) {
              return (
                <>
                  {!!totalData.payNow && (
                    <PaymentRow
                      label={`${payNowLabel} ${depositSuffix}`}
                      amount={totalData.payNow}
                      currency={totalCurrency}
                      isNewExperimentVariant={isNewExperimentVariant}
                    />
                  )}
                  {!!totalData.payLate && (
                    <PaymentRow
                      label={formatMessageCapitalized(
                        { id: 'policies.pay-on' },
                        { date: formatDate(aggregatedData.payDate || '') }
                      )}
                      amount={totalData.payLate}
                      currency={totalCurrency}
                      isNewExperimentVariant={isNewExperimentVariant}
                    />
                  )}
                </>
              )
            }

            switch (paymentDisplayType) {
              case PAYMENT_DISPLAY_TYPES.NON_REFUNDABLE:
              case PAYMENT_DISPLAY_TYPES.PAY_UPON_BOOKING_WITH_LATER_PAYMENT:
                return payNowWithInsurance > 0
                  ? renderPayNowRow(payNowWithInsurance, totalCurrency)
                  : null

              case PAYMENT_DISPLAY_TYPES.PAY_ON_ARRIVAL_ONLY:
                return (
                  <PaymentRow
                    label={formatMessageCapitalized(
                      { id: 'policies.pay-on' },
                      { date: formattedPayDate }
                    )}
                    amount={totalPrice}
                    currency={totalCurrency}
                    isNewExperimentVariant={isNewExperimentVariant}
                  />
                )

              case PAYMENT_DISPLAY_TYPES.STAY_FOR_LONG_DEPOSIT:
                return (
                  <>
                    <PaymentRow
                      label={`${payNowLabel} ${depositSuffix}`}
                      amount={payNowWithInsurance}
                      currency={totalCurrency}
                      isNewExperimentVariant={isNewExperimentVariant}
                    />
                    <PaymentRow
                      label={formatMessageCapitalized(
                        { id: 'policies.pay-on' },
                        { date: formattedPayDate }
                      )}
                      amount={payLate ?? undefined}
                      currency={totalCurrency}
                      isNewExperimentVariant={isNewExperimentVariant}
                    />
                  </>
                )

              default:
                return (
                  <>
                    {payNowWithInsurance > 0 && renderPayNowRow(payNowWithInsurance, totalCurrency)}
                    {renderPaymentsByDate()}
                  </>
                )
            }
          })()}

          {displayPayAtPropertySection && (
            <PaymentRow
              label={formatMessageCapitalized({ id: 'tax-breakdown.pay-at-hotel' })}
              amount={paymentTotals.payLater}
              currency={paymentTotals.currency || totalCurrency}
              withTaxDetails={displayTaxDetailsButton}
              taxDetails={groupedTaxDetails}
              isNewExperimentVariant={isNewExperimentVariant}
            />
          )}
        </div>
      </div>

      <div className={styles.content}>
        <Typography variant="caption" size="l" bold>
          {`${formatMessage({ id: 'global.no-conversion-fees' })}`}
        </Typography>
        <Typography variant="caption" size="l">
          {` ${localCurrencyText}`}
        </Typography>
      </div>
    </>
  )
}

PriceDetailsContent.displayName = 'PriceDetailsContent'
